import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
} from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ED_LANDING_PAGE,
  MHL_LANDING_PAGE,
  PE_LANDING_PAGE,
  WL_LANDING_PAGE,
} from '@/lib/links'
import footerBackground from '@/images/footer-bg-fable.jpg'
import clsx from 'clsx'
import { Plus_Jakarta_Sans } from 'next/font/google'
import Link from 'next/link'
import { HTMLAttributeAnchorTarget, ReactNode } from 'react'

import { LegitScriptSealLink } from '@/shared/components/LegitScriptSealLink'

const plusJakartaSans = Plus_Jakarta_Sans({
  subsets: ['latin'],
  display: 'swap',
})

const footerNavigation = [
  {
    title: 'Services',
    links: [
      ['Medical certificates', '/medical-certificates-online'],
      ['Carer certificates', '/carers-certificates-online'],
      ['2-day medical certificates', '/two-day-medical-certificate-online'],
      ['Prescriptions', '/prescriptions-online'],
      ['Specialist referrals', '/specialist-referral-online'],
      ['Telehealth consultations', '/telehealth-consultation'],
    ],
  },
  {
    title: 'Treatment plans',
    links: [
      ['Weight loss', WL_LANDING_PAGE],
      ['Erectile dysfunction', ED_LANDING_PAGE],
      ['Premature ejaculation', PE_LANDING_PAGE],
      ['Male hair loss', MHL_LANDING_PAGE],
    ],
  },
  {
    title: 'Tests & referrals',
    links: [
      ['Blood tests', '/blood-tests-online'],
      ['STD/STI tests', '/blood-tests-online/std-tests'],
      ['Radiology referrals', '/radiology-referrals-online'],
    ],
  },
  {
    title: 'Learn',
    links: [
      ['Blog', '/blog'],
      ['FAQ', '/faqs'],
      ['Pathology & radiology results', '/pathology-radiology-results'],
    ],
  },
] as const

const socialLinks = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61555290025580',
    icon: faFacebookF,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/nextclinic.com.au/',
    icon: faInstagram,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/nextclinic',
    icon: faLinkedinIn,
  },
] as const

export type FooterProps = {
  className?: string
}

export function Footer({ className }: FooterProps) {
  return (
    <footer
      className={clsx('font-sans text-white', className)}
      style={{
        backgroundColor: '#123bb4',
        backgroundImage: `url(${footerBackground.src})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <div className="mx-auto flex max-w-[1440px] flex-col gap-8 px-6 pt-12 xl:gap-14 xl:px-[140px] xl:pt-[88px]">
        <div className="flex flex-col gap-8 xl:flex-row xl:justify-between xl:gap-0">
          <div className="flex flex-col gap-2.5 xl:w-[300px] xl:gap-3">
            <Link
              href="/"
              aria-label="NextClinic home"
              className="flex w-fit items-center gap-[9px] rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white xl:gap-2.5"
            >
              <NextClinicMark className="h-6 w-[26px] xl:h-7 xl:w-[30px]" />
              <span className="text-[19px] font-bold leading-6 tracking-[-0.01em] xl:text-[22px] xl:leading-7">
                nextclinic
              </span>
            </Link>

            <div className="mt-1 flex flex-col gap-[3px] text-[13px] leading-5 text-white/[0.65] xl:mt-2 xl:gap-1 xl:text-sm xl:leading-[22px]">
              <p>ABN: 69673211704</p>
              <a
                href="mailto:support@nextclinic.com.au"
                className="w-fit rounded-sm transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                support@nextclinic.com.au
              </a>
            </div>

            <LegitScriptSealLink
              className="mt-1.5 h-24 w-[88px] rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white xl:mt-2 xl:h-[118px] xl:w-[108px]"
              imageWidth={108}
              imageHeight={118}
              imageClassName="h-full w-full object-contain object-left"
            />
          </div>

          <nav
            aria-label="Footer navigation"
            className="grid grid-cols-2 gap-x-6 gap-y-8 xl:w-[616px] xl:grid-cols-[280px_280px] xl:gap-x-14 xl:gap-y-11"
          >
            {footerNavigation.map((group) => (
              <div
                key={group.title}
                className="flex flex-col gap-3 xl:gap-[14px]"
              >
                <h2 className="text-[11px] font-semibold uppercase leading-[14px] tracking-[0.05em] text-white/50 xl:text-xs xl:leading-4">
                  {group.title}
                </h2>
                {group.links.map(([text, href]) => (
                  <FooterLink key={href} href={href}>
                    {text}
                  </FooterLink>
                ))}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex flex-col gap-4 border-t border-white/15 pt-5 xl:flex-row xl:items-center xl:justify-between xl:gap-0 xl:pt-[22px]">
          <div className="flex items-center gap-5 text-[13px] leading-4 text-white/[0.85] xl:gap-7 xl:text-sm xl:leading-[18px]">
            <FooterLink href="/page/terms-of-service" regular>
              Terms of Service
            </FooterLink>
            <FooterLink href="/page/privacy-policy" regular>
              Privacy Policy
            </FooterLink>
          </div>

          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:gap-5">
            <div className="flex w-fit items-center gap-2 xl:gap-2.5">
              {socialLinks.map(({ label, href, icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white xl:h-9 xl:w-9"
                >
                  <FontAwesomeIcon
                    icon={icon}
                    className="h-[15px] w-[15px] xl:h-4 xl:w-4"
                  />
                </a>
              ))}
            </div>
            <p className="text-[13px] leading-4 text-white/[0.65] xl:text-sm xl:leading-[18px]">
              © {new Date().getFullYear()} NextClinic. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <div
        aria-hidden="true"
        className="mt-8 w-full select-none overflow-hidden xl:mt-14"
      >
        <p
          className={clsx(
            plusJakartaSans.className,
            '-mb-[2%] w-max whitespace-nowrap text-[22.4vw] font-bold leading-[21.333vw] tracking-[-0.03em] text-white xl:text-[22.5vw] xl:leading-[20.833vw]',
          )}
        >
          nextclinic
        </p>
      </div>
    </footer>
  )
}

function FooterLink({
  children,
  href,
  regular = false,
  target,
}: {
  children: ReactNode
  href: string
  regular?: boolean
  target?: HTMLAttributeAnchorTarget
}) {
  return (
    <Link
      href={href}
      className={clsx(
        'w-fit rounded-sm underline-offset-4 transition-opacity hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
        regular
          ? 'font-normal'
          : 'text-[13px] font-medium leading-[17px] text-white xl:text-sm xl:leading-[18px]',
      )}
      target={target}
      rel={target === '_blank' ? 'noreferrer' : undefined}
    >
      {children}
    </Link>
  )
}

function NextClinicMark({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 262.21 243.21"
      className={clsx('shrink-0', className)}
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="214.28" cy="195.28" r="47.93" fill="#00ffff" />
      <path
        d="m125.88,21.8c-6.79,10.4-7.45,21.05-7.41,26.09.98,6.63,2.42,22.65-5.59,40.22-2.48,5.43-11.09,22.47-29.62,32.33-15.29,8.13-20.65,2.5-40.9,11.22-8.99,3.87-20.38,8.69-29.08,19.54-.93,1.16-22.05,28.44-9.06,57.9,10.48,23.78,36.67,34.15,57.99,32.03,26-2.58,40.96-23.38,42.01-24.88,16.28-23.38.67-40.22,17.79-69.88,4.02-6.97,13.39-23.2,31.46-30.51,14.82-6,22.11-.59,38.13-7.72,4.71-2.1,18.87-9.16,27.74-24.4,9.04-15.54,13.68-41.47-1.55-62.28C197.82-5.79,161.93.5,159.82.91c-5.29,1.02-23.22,4.48-33.94,20.89Z"
        fill="#ffffff"
      />
    </svg>
  )
}
